"use client";

/**
 * The explanation panel body (mission drilldown-substrate, WP05/T018) — mounted by `AppShell`
 * inside the AppLayout `splitPanel` slot, content driven entirely by `ExplainPanelContext`.
 * Anatomy per research R5:
 *
 * 1. Header — artefact title, row/column labels, the figure with its Reproduced/Computed badge
 *    (`StatusBadge`, the existing vocabulary — never a new status chip).
 * 2. Calc steps — ordered `ExpandableSection`s (title, plain-language summary, rule reference);
 *    each has a "view affected rows" action that sets the contributing-rows step filter.
 * 3. Contributing sites — a Cloudscape `Table` + `Pagination` bound to the SERVER page
 *    (`contributing_rows.page`/`.total`) — never client-side re-sorted (the server's own
 *    contribution-descending, row_key-ascending order is preserved verbatim).
 * 4. References footer — methodology wording via the SAME `HelpPanel` mechanism the artefact's
 *    own "Info" link uses (`openWithContent`, keyed off `ARTEFACT_NARRATIVES[pdfRef]` — FR-007:
 *    wording via reference, never duplicated into lineage rows) + an explicit "View in
 *    configuration" link — role-aware (WP05 cycle-2, Issue 4): `/admin/config` is gated by
 *    `(auth)/admin/layout.tsx` to `super_admin` only, so linking every role there ejects viewer
 *    (e.g. GLA planner) sessions to `/dashboard` — SC-005 requires the config reference to work
 *    identically for every role. The one viewer-accessible config-disclosure surface
 *    (`features/report/Disclosure.tsx`, opened from each report page's own `ConfigHeader`) is a
 *    per-page Modal, not a standalone route this globally-mounted panel can link to — so for
 *    every role except `super_admin` this shows the reference text only (the "Configuration
 *    version N" line above already states it), never a link that would eject the viewer.
 *
 * `not_emitted` → the Domain Language's binding copy: "explanation not yet available for this
 * figure" (spec.md, never "drill-down"/"lineage"/"source rows"). Loading/error states come
 * straight from `useExplain`'s TanStack Query result — error renders the thrown message, never
 * an eternal spinner.
 *
 * Local `page`/`stepKeyFilter` state resets to defaults whenever the EXPLAINED FIGURE's own
 * identity changes (a fresh click on a different cell) — switching pages/filters for the current
 * figure must not silently carry over to a different one.
 */

import { useState } from "react";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Header from "@cloudscape-design/components/header";
import Link from "@cloudscape-design/components/link";
import Pagination from "@cloudscape-design/components/pagination";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Table from "@cloudscape-design/components/table";

import { useAuth } from "@/features/auth/useAuth";
import { StatusBadge } from "@/features/dashboard/report/StatusBadge";
import { formatCount } from "@/features/report/components/formatting";
import { useHelpPanel } from "@/features/help/HelpPanelContext";
import { ARTEFACT_NARRATIVES } from "@/features/report/narrative";
import { PAGINATION_ARIA_LABELS } from "@/lib/paginationAriaLabels";

import type {
  CalcStep,
  CellAddress,
  ContributingRow,
  ExplainResponse,
} from "./explainTypes";
import { isNotEmitted } from "./explainTypes";
import { useExplainPanel } from "./ExplainPanelContext";
import { useExplain } from "./useExplain";
import { withBaselineYear } from "../narrative/baselineYear";

const PAGE_SIZE = 50;

function requestIdentity(runId: string, address: CellAddress): string {
  return [
    runId,
    address.component,
    address.key,
    address.metric,
    address.year,
    address.scenario,
  ].join("|");
}

/** The union of every year key present across the CURRENT page's rows (each row's
 * `payload.years` is a `Record<string, number>`, per the contract example) — sorted ascending
 * so columns read left-to-right chronologically regardless of row order. */
function contributingYears(items: readonly ContributingRow[]): string[] {
  const years = new Set<string>();
  for (const item of items) {
    const yearsField = item.payload["years"];
    if (yearsField && typeof yearsField === "object") {
      for (const key of Object.keys(yearsField as Record<string, unknown>)) {
        years.add(key);
      }
    }
  }
  return Array.from(years).sort();
}

function yearValue(
  payload: Record<string, unknown>,
  year: string,
): number | null {
  const yearsField = payload["years"];
  if (!yearsField || typeof yearsField !== "object") return null;
  const value = (yearsField as Record<string, unknown>)[year];
  return typeof value === "number" ? value : null;
}

function numericPayloadValue(
  payload: Record<string, unknown>,
  key: string,
): number | null {
  const value = payload[key];
  return typeof value === "number" ? value : null;
}

function hasBand(items: readonly ContributingRow[]): boolean {
  return items.some((item) => typeof item.payload["band"] === "string");
}

function CalcStepSection({
  step,
  onViewAffectedRows,
}: {
  step: CalcStep;
  onViewAffectedRows: (stepKey: string) => void;
}): React.JSX.Element {
  return (
    <ExpandableSection headerText={step.title} variant="footer">
      <SpaceBetween size="s">
        <Box variant="p">{step.summary}</Box>
        <Box variant="small" color="text-status-inactive">
          Rule: {step.rule_ref.rule_domain ?? "—"}
          {step.rule_ref.rule_target_key
            ? ` — ${step.rule_ref.rule_target_key}`
            : ""}
        </Box>
        <Button
          variant="inline-link"
          onClick={() => onViewAffectedRows(step.step_key)}
        >
          View affected rows
        </Button>
      </SpaceBetween>
    </ExpandableSection>
  );
}

interface ExplainedContentProps {
  result: ExplainResponse;
  page: number;
  onPageChange: (page: number) => void;
  stepKeyFilter: string | null;
  onStepKeyFilterChange: (stepKey: string | null) => void;
  pdfRef: string;
  rowLabel: string;
  columnLabel: string;
  artefactTitle: string;
}

function ExplainedContent({
  result,
  page,
  onPageChange,
  stepKeyFilter,
  onStepKeyFilterChange,
  pdfRef,
  rowLabel,
  columnLabel,
  artefactTitle,
}: ExplainedContentProps): React.JSX.Element {
  const { openWithContent } = useHelpPanel();
  const { user } = useAuth();
  const narrative = ARTEFACT_NARRATIVES[pdfRef];
  const canViewAdminConfig = user?.role === "super_admin";

  const years = contributingYears(result.contributing_rows.items);
  const showBand = hasBand(result.contributing_rows.items);
  const pagesCount = Math.max(
    1,
    Math.ceil(
      result.contributing_rows.total / result.contributing_rows.page_size,
    ),
  );
  const orderedSteps = [...result.calc_steps].sort(
    (a, b) => a.step_order - b.step_order,
  );

  return (
    <SpaceBetween size="l">
      <SpaceBetween size="xs">
        <Header variant="h3">{artefactTitle}</Header>
        <Box variant="p">
          {rowLabel}, {columnLabel}
        </Box>
        <SpaceBetween size="xs" direction="horizontal">
          <Box variant="awsui-value-large">
            {formatCount(result.figure.value)}
          </Box>
          <StatusBadge status={result.figure.status} />
        </SpaceBetween>
      </SpaceBetween>

      <SpaceBetween size="s">
        <Header variant="h3">Calculation steps</Header>
        {orderedSteps.length === 0 ? (
          <Box variant="p" color="text-status-inactive">
            No calculation steps recorded for this figure.
          </Box>
        ) : (
          orderedSteps.map((step) => (
            <CalcStepSection
              key={step.step_key}
              step={step}
              onViewAffectedRows={(stepKey) => {
                onStepKeyFilterChange(stepKey);
                onPageChange(1);
              }}
            />
          ))
        )}
      </SpaceBetween>

      {/* WP06: the figure and its calc steps above are the explanation proper and
          always render. The contributing SITES are facility-grain — the site name
          travels in `row_label`, which is exactly how this leaked — so without the
          entitlement the section is absent, not empty, and silently so (FR-017). */}
      {!result.contributing_rows.facility_rows_withheld && (
        <SpaceBetween size="s">
          <Header
            variant="h3"
            description={
              stepKeyFilter
                ? `Filtered to rows affected by "${stepKeyFilter}"`
                : undefined
            }
            actions={
              stepKeyFilter ? (
                <Button
                  variant="inline-link"
                  onClick={() => {
                    onStepKeyFilterChange(null);
                    onPageChange(1);
                  }}
                >
                  Clear filter
                </Button>
              ) : undefined
            }
          >
            Contributing sites
          </Header>
          <Table
            variant="embedded"
            items={result.contributing_rows.items}
            trackBy="row_key"
            columnDefinitions={[
              {
                id: "site",
                header: "Site",
                cell: (row: ContributingRow) => row.row_label,
              },
              {
                id: "permit",
                header: "Permit",
                cell: (row: ContributingRow) => row.row_key,
              },
              ...(showBand
                ? [
                    {
                      id: "band",
                      header: "Band",
                      cell: (row: ContributingRow) =>
                        typeof row.payload["band"] === "string"
                          ? (row.payload["band"] as string)
                          : "—",
                    },
                  ]
                : []),
              ...years.map((year) => ({
                id: `year-${year}`,
                header: year,
                cell: (row: ContributingRow) => {
                  const v = yearValue(row.payload, year);
                  return v === null ? "—" : formatCount(v);
                },
              })),
              {
                id: "contribution",
                header: "Contribution",
                cell: (row: ContributingRow) => {
                  const v = numericPayloadValue(row.payload, "contribution");
                  return v === null ? "—" : formatCount(v);
                },
              },
            ]}
            ariaLabels={{ tableLabel: "Contributing sites" }}
            empty={
              <Box textAlign="center" color="text-body-secondary">
                No contributing rows for this figure.
              </Box>
            }
            pagination={
              <Pagination
                currentPageIndex={page}
                pagesCount={pagesCount}
                onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
                ariaLabels={PAGINATION_ARIA_LABELS}
              />
            }
          />
        </SpaceBetween>
      )}

      <SpaceBetween size="xs">
        <Header variant="h3">References</Header>
        <Box variant="p" color="text-status-inactive">
          Configuration version {result.references.config_version ?? "—"}
          {result.references.spec_ref ? ` · ${result.references.spec_ref}` : ""}
        </Box>
        <SpaceBetween size="xs" direction="horizontal">
          {narrative ? (
            <Link
              variant="info"
              onFollow={() =>
                openWithContent({
                  title: pdfRef,
                  definition: withBaselineYear(narrative.lead),
                  methodology: withBaselineYear(narrative.narrative),
                })
              }
            >
              Methodology
            </Link>
          ) : null}
          {canViewAdminConfig ? (
            <Link href="/admin/config">View in configuration</Link>
          ) : null}
        </SpaceBetween>
      </SpaceBetween>
    </SpaceBetween>
  );
}

export function ExplainPanel(): React.JSX.Element {
  const { request } = useExplainPanel();
  const [page, setPage] = useState(1);
  const [stepKeyFilter, setStepKeyFilter] = useState<string | null>(null);
  const [priorIdentity, setPriorIdentity] = useState<string | null>(null);

  // Switching to a DIFFERENT figure (a fresh cell click) resets paging/step-filter — never
  // carries over from whatever the previous explanation happened to be showing. The
  // React-recommended "adjust state during rendering" pattern (react.dev, "You Might Not Need
  // an Effect" → "Adjusting some state when a prop changes") rather than a `useEffect` with a
  // synchronous `setState` in its body (flagged by this repo's `react-hooks/set-state-in-effect`
  // lint rule, and itself an extra render pass an in-render adjustment avoids).
  const identity = request
    ? requestIdentity(request.runId, request.address)
    : null;
  if (identity !== priorIdentity) {
    setPriorIdentity(identity);
    setPage(1);
    setStepKeyFilter(null);
  }

  const { data, isPending, isError, error } = useExplain({
    runId: request?.runId ?? null,
    address: request?.address ?? null,
    page,
    pageSize: PAGE_SIZE,
    stepKey: stepKeyFilter,
  });

  if (!request) {
    return (
      <Box variant="p" color="text-status-inactive">
        Select an explainable figure to see how it was calculated.
      </Box>
    );
  }

  if (isPending) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />{" "}
        <Box variant="p" display="inline">
          Loading explanation…
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert type="error" header="Couldn&rsquo;t load the explanation">
        {error instanceof Error ? error.message : "Something went wrong."}
      </Alert>
    );
  }

  if (!data) return <></>;

  if (isNotEmitted(data)) {
    return (
      <SpaceBetween size="s">
        <Header variant="h3">{request.artefactTitle}</Header>
        <Box variant="p">
          {request.rowLabel}, {request.columnLabel}
        </Box>
        <Alert
          type="info"
          header="Explanation not yet available for this figure"
        >
          This figure does not yet have a recorded explanation.
        </Alert>
      </SpaceBetween>
    );
  }

  return (
    <ExplainedContent
      result={data}
      page={page}
      onPageChange={setPage}
      stepKeyFilter={stepKeyFilter}
      onStepKeyFilterChange={setStepKeyFilter}
      pdfRef={request.pdfRef}
      rowLabel={request.rowLabel}
      columnLabel={request.columnLabel}
      artefactTitle={request.artefactTitle}
    />
  );
}
